import React, { type JSX } from 'react';
import { IconButton, Stack, Tooltip } from '@mui/material';
import { Lock, LockOpen } from '@mui/icons-material';
import { ConfirmModal, CustomTransComponent } from 'components';
import { styled } from '@mui/material/styles';
import { useTypedSelector, useAppDispatch } from '@fiji/common/src/app/store';
import { setMessageContent } from '@fiji/common/src/features/common/commonSlice';
import { useRogueDevicesMutation } from '@fiji/common/src/features/dashboardManagement/dashboardApi';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useGetDeviceByIdQuery } from '@fiji/common/src/features/deviceManagement/deviceApi';

export const Quarantine = (): JSX.Element => {
    const { deviceId } = useParams();
    const dispatch = useAppDispatch();
    const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false);
    const isQuarantine = useTypedSelector((state: any) => state?.common.selectedNode)?.deviceStatus === 'Quarantined';
    const [updateQuarantine, { isLoading: rogueLoading }] = useRogueDevicesMutation();

    const { t } = useTranslation();

    const { data: deviceData } = useGetDeviceByIdQuery(deviceId, {});

    const CustomTooltip = styled(Tooltip)(() => ({
        '& .MuiTooltip-tooltip': {
            backgroundColor: '#000000b3',
            color: '#fff',
        },
        '& .MuiTooltip-arrow': {
            color: '#000000b3',
        },
    }));

    const dialogTitle = isQuarantine ? (
        <CustomTransComponent translationKey={'ROGUE_DEVICES:REMOVE_QUARANTINE_BTN'} />
    ) : (
        <CustomTransComponent
            translationKey={'ROGUE_DEVICES:QUARANTINED_DEVICE_NAME'}
            replace={{ deviceName: deviceData?.data?.name }}
        />
    );

    const dialogDescription = isQuarantine ? (
        <CustomTransComponent
            translationKey={'ROGUE_DEVICES:REMOVE_QUARANTINE_DESCRIPTION'}
            replace={{ deviceName: deviceData?.data?.name }}
        />
    ) : (
        <CustomTransComponent
            translationKey={'ROGUE_DEVICES:QUARANTINE_DESCRIPTION'}
            replace={{ deviceName: deviceData?.data?.name }}
        />
    );

    const confirmButtonText = isQuarantine ? (
        <CustomTransComponent translationKey={'ROGUE_DEVICES:REMOVE_QUARANTINE'} />
    ) : (
        <CustomTransComponent translationKey={'ROGUE_DEVICES:QUARANTINE_DEVICE'} />
    );

    const toggleLockUnlock = async (): Promise<void> => {
        const { error }: any = await updateQuarantine({
            body: {
                deviceIds: [deviceId],
                status: !isQuarantine,
            },
        });

        if (!error) {
            setIsModalOpen(false);
            dispatch(
                setMessageContent({
                    isOpen: true,
                    message: !isQuarantine
                        ? t('ROGUE_DEVICES:DEVICE_QUARANTINED_SUCCESS_MSG')
                        : t('ROGUE_DEVICES:DEVICE_UNQUARANTINED_SUCCESSS_MSG'),
                })
            );
        }
    };
    return (
        <Stack direction="row" alignItems="center" mr={2}>
            <CustomTooltip
                title={
                    isQuarantine ? (
                        <CustomTransComponent
                            translationKey={'ROGUE_DEVICES:REMOVE_QUARANTINE'}
                            replace={{ deviceName: deviceData?.data?.name }}
                        />
                    ) : (
                        <CustomTransComponent
                            translationKey={'ROGUE_DEVICES:QUARANTINE_DEVICE_NAME'}
                            replace={{ deviceName: deviceData?.data?.name }}
                        />
                    )
                }
                placement="bottom"
            >
                <IconButton id="unsnooze-button" color="inherit" size="large" onClick={() => setIsModalOpen(true)}>
                    {!isQuarantine ? <LockOpen /> : <Lock />}
                </IconButton>
            </CustomTooltip>
            <ConfirmModal
                type="primary"
                isVisible={isModalOpen}
                header={dialogTitle}
                description={dialogDescription}
                isLoading={rogueLoading}
                actionButtonText={confirmButtonText}
                actionButtonIcon={!isQuarantine ? <Lock /> : <LockOpen />}
                confirmClick={toggleLockUnlock}
                onCancel={(): void => setIsModalOpen(false)}
                disableCancelOnLoading={rogueLoading}
                buttonColor={'error'}
            />
        </Stack>
    );
};
